// Works with:
// allowedRoles: ["Super Admin", "Manager"] OR [13, 99] OR [{ Id: 13 }, { Name: "Manager" }]
// userRoles: your array of { Id, Name } objects (or strings/ids)

type NavigationChild = {
  id: string;
  route: string;
  name: string;
  Icon: any;
  allowed?: any;
  matchRoutes?: string[];
};

export type NavigationItem = {
  id: string;
  route?: string;
  name: string;
  Icon: any;
  allowed?: any;
  children?: NavigationChild[];
  matchRoutes?: string[];
};

export type FlattenedNavigationItem = {
  Icon: any;
  id: string;
  name: string;
  parentName?: string;
  route: string;
};

const normalizePath = (value?: string) => {
  if (!value) return "/";
  const [pathname] = value.split(/[?#]/);
  if (!pathname) return "/";
  const trimmed = pathname.replace(/\/+$/, "");
  return trimmed || "/";
};

export const getInitials = (name?: string) => {
  if (!name) return "NA";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
};

export const getPrimaryRoleLabel = (roles: any) => {
  const [firstRole] = Array.isArray(roles) ? roles : [roles];

  if (typeof firstRole === "string") return firstRole;
  if (typeof firstRole === "number") return `Role ${firstRole}`;
  if (firstRole && typeof firstRole === "object") {
    return firstRole.Name ?? firstRole.name ?? `Role ${firstRole.Id ?? ""}`;
  }

  return "User";
};

export const isRouteMatch = (pathname: string, pattern?: string) => {
  if (!pattern || pattern === "#") return false;

  const currentPath = normalizePath(pathname);
  const normalizedPattern = normalizePath(
    pattern.endsWith("/*") ? pattern.slice(0, -2) : pattern
  );

  if (pattern.endsWith("/*")) {
    return (
      currentPath === normalizedPattern ||
      currentPath.startsWith(`${normalizedPattern}/`)
    );
  }

  return currentPath === normalizedPattern;
};

export const matchesAnyRoute = (pathname: string, patterns?: string[]) =>
  (patterns ?? []).some((pattern) => isRouteMatch(pathname, pattern));

export const hasPermission = (allowedRoles: any, userRoles: any): boolean => {
  const allowed = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];
  const user = Array.isArray(userRoles) ? userRoles : [userRoles];

  if (!allowed.length || !user.length) return false;

  // 1) Global allow if any allowed role name contains "Default"
  const containsDefault = (r: any) => {
    if (typeof r === "string") return r.toLowerCase().includes("default");
    if (typeof r === "object") {
      const n = (r.Name ?? r.name ?? "").toString().toLowerCase();
      return n.includes("default");
    }
    return false;
  };
  if (allowed.some(containsDefault)) return true;

  // 2) Otherwise do normal matching (by id or name)
  const toKeySet = (arr: any[]) => {
    const keys = new Set<string>();
    for (const r of arr) {
      if (r == null) continue;
      if (typeof r === "number") keys.add(`id:${r}`);
      else if (typeof r === "string")
        keys.add(`name:${r.trim().toLowerCase()}`);
      else if (typeof r === "object") {
        if (r.Id != null) keys.add(`id:${Number(r.Id)}`);
        if (r.id != null) keys.add(`id:${Number(r.id)}`);
        const nm = (r.Name ?? r.name)?.toString().trim().toLowerCase();
        if (nm) keys.add(`name:${nm}`);
      }
    }
    return keys;
  };

  const allowedSet = toKeySet(allowed);
  const userSet = toKeySet(user);
  for (const k of userSet) if (allowedSet.has(k)) return true;
  return false;
};

export const isLeafItemActive = (
  pathname: string,
  item: Pick<NavigationItem, "route" | "matchRoutes">
) => {
  return isRouteMatch(pathname, item.route) || matchesAnyRoute(pathname, item.matchRoutes);
};

export const isNavigationItemActive = (
  pathname: string,
  item: NavigationItem
) => {
  if (item.children?.length) {
    return (
      matchesAnyRoute(pathname, item.matchRoutes) ||
      item.children.some((child) => isLeafItemActive(pathname, child))
    );
  }

  return isLeafItemActive(pathname, item);
};

export const findActiveParentId = (
  items: NavigationItem[],
  pathname: string
) => {
  const activeParent = items.find(
    (item) => item.children?.length && isNavigationItemActive(pathname, item)
  );

  return activeParent?.id ?? null;
};

export const flattenNavigationItems = (
  items: NavigationItem[]
): FlattenedNavigationItem[] => {
  return items.flatMap((item) => {
    if (item.children?.length) {
      return item.children
        .filter((child) => Boolean(child.route && child.route !== "#"))
        .map((child) => ({
          Icon: child.Icon,
          id: child.id,
          name: child.name,
          parentName: item.name,
          route: child.route,
        }));
    }

    if (!item.route || item.route === "#") return [];

    return [
      {
        Icon: item.Icon,
        id: item.id,
        name: item.name,
        route: item.route,
      },
    ];
  });
};
